import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Layout } from '@/components/layout'
import { useAuditLogs, useAuditActions, useExportAuditLogs } from '@/api/audit-logs'
import { useUsers } from '@/api/users'
import { useCrises } from '@/api/crisis'
import {
  FileText,
  Search,
  Download,
  RefreshCw,
  Eye,
  Calendar,
  User,
  Shield,
  Activity,
} from 'lucide-react'
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Label } from '@/components/ui/label'
import { useToast } from '@/lib/hooks/useToast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { AuditLog, User as AuditUser } from '@/lib/types'

const DEFAULT_PER_PAGE = 50

const AUDIT_RESOURCE_TYPES = [
  'User',
  'Crisis',
  'Scan',
  'Task',
  'File',
  'BobAnalyse',
  'WritingAssistant',
  'Role',
  'Permission',
  'APIToken',
  'System',
  'PracticeCase',
] as const

const PER_PAGE_OPTIONS = ['10', '20', '50', '100'] as const

const getInitialAuditViewState = () => {
  const params = new URLSearchParams(window.location.search)
  const pageParam = Number(params.get('page') ?? '1')
  const perPageParam = params.get('per_page') ?? String(DEFAULT_PER_PAGE)

  return {
    searchQuery: params.get('q') ?? '',
    currentPage: Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1,
    perPage: PER_PAGE_OPTIONS.includes(perPageParam as (typeof PER_PAGE_OPTIONS)[number])
      ? Number(perPageParam)
      : DEFAULT_PER_PAGE,
    filters: {
      user_id: params.get('user_id') ?? '',
      crisis_id: params.get('crisis_id') ?? '',
      action: params.get('action') ?? 'all',
      resource_type: params.get('resource_type') ?? 'all',
      start_date: params.get('start_date') ?? '',
      end_date: params.get('end_date') ?? '',
    },
  }
}

export function AdminAuditLogsPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const initialViewState = getInitialAuditViewState()
  const [searchQuery, setSearchQuery] = useState(initialViewState.searchQuery)
  const [currentPage, setCurrentPage] = useState(initialViewState.currentPage)
  const [perPage, setPerPage] = useState(initialViewState.perPage)
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)

  // Permission checks
  const canExportAudit = useHasPermission('audit.export')

  // Filter states
  const [filters, setFilters] = useState(initialViewState.filters)

  const {
    data: logsData,
    isLoading,
    refetch,
  } = useAuditLogs(currentPage, perPage, {
    ...Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== 'all')
    ),
  })

  const { data: actionsData } = useAuditActions()
  const { mutateAsync: exportLogs, isPending: isExporting } = useExportAuditLogs()
  const { data: usersData } = useUsers({ page: 1, per_page: 200 })
  const { data: crisesData } = useCrises()

  const usersById = new Map((usersData?.users ?? []).map(user => [user.id, user]))
  const crisesById = new Map((crisesData?.data ?? []).map(crisis => [crisis.id, crisis]))

  const getResolvedUser = (log: AuditLog): AuditUser | undefined => usersById.get(log.userId)

  const getFallbackEmail = (log: AuditLog) =>
    log.description?.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] ?? null

  const getUserDisplayName = (log: AuditLog) => {
    const user = getResolvedUser(log)

    if (user?.displayName?.trim()) {
      return user.displayName
    }

    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()

    if (fullName) {
      return fullName
    }

    if (user?.email) {
      return user.email
    }

    return getFallbackEmail(log) ?? 'Onbekende gebruiker'
  }

  const getUserSecondaryLabel = (log: AuditLog) => {
    const user = getResolvedUser(log)
    const displayName = getUserDisplayName(log)

    if (user?.email && user.email !== displayName) {
      return user.email
    }

    return null
  }

  const getActionLabel = (action: string) => {
    const translationKey = `admin.audit.actions.${action.toUpperCase()}`
    const translated = t(translationKey)

    if (translated !== translationKey) {
      return translated
    }

    return action.replace(/_/g, ' ').toLowerCase()
  }

  const getAuditDetails = (log: AuditLog): Record<string, unknown> | null => {
    if (log.details && typeof log.details === 'object' && !Array.isArray(log.details)) {
      return log.details as Record<string, unknown>
    }

    return null
  }

  const getDetailString = (log: AuditLog, ...keys: string[]) => {
    const details = getAuditDetails(log)

    for (const key of keys) {
      const value = details?.[key]

      if (typeof value === 'string' && value.trim()) {
        return value.trim()
      }
    }

    return null
  }

  const getDetailStringArray = (log: AuditLog, key: string) => {
    const value = getAuditDetails(log)?.[key]

    if (!Array.isArray(value)) {
      return []
    }

    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
  }

  const humanizeToken = (value: string) =>
    value
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()

  const formatShortResourceId = (value: string | null | undefined) =>
    value ? `#${value.slice(0, 8)}` : null

  const formatListSummary = (items: string[]) => {
    const normalizedItems = items
      .map(item => humanizeToken(item))
      .filter((item, index, array) => item && array.indexOf(item) === index)

    if (normalizedItems.length === 0) {
      return null
    }

    if (normalizedItems.length <= 2) {
      return normalizedItems.join(', ')
    }

    return `${normalizedItems.slice(0, 2).join(', ')} +${normalizedItems.length - 2}`
  }

  const getDescriptionIdentifier = (log: AuditLog) => {
    const description = log.description?.trim()

    if (!description) {
      return null
    }

    const quotedMatch = description.match(/'([^']+)'|"([^"]+)"/)
    if (quotedMatch) {
      return (quotedMatch[1] ?? quotedMatch[2] ?? '').trim()
    }

    const colonIndex = description.indexOf(':')
    if (colonIndex >= 0) {
      return description.slice(colonIndex + 1).trim()
    }

    return null
  }

  const getActionChipLabel = (action: string) => {
    const normalizedAction = action.toLowerCase()

    if (normalizedAction.includes('created')) return 'Aangemaakt'
    if (normalizedAction.includes('updated')) return 'Bijgewerkt'
    if (normalizedAction.includes('deleted')) return 'Verwijderd'
    if (normalizedAction.includes('activated')) return 'Geactiveerd'
    if (normalizedAction.includes('deactivated')) return 'Gedeactiveerd'
    if (normalizedAction.includes('login')) return 'Inloggen'
    if (normalizedAction.includes('logout')) return 'Uitloggen'
    if (normalizedAction.includes('denied')) return 'Geweigerd'

    return getActionLabel(action)
  }

  const getResourceTypeLabel = (resourceType: string | null | undefined) => {
    if (!resourceType) {
      return null
    }

    const translationKey = `admin.audit.resource${resourceType}`
    const translated = t(translationKey)

    if (translated !== translationKey) {
      return translated
    }

    return resourceType
  }

  const getResourceEventLabel = (log: AuditLog) => {
    switch (log.resourceType?.toLowerCase()) {
      case 'crisis':
        return 'crisis'
      case 'bobanalyse':
        return 'BOB-analyse'
      case 'scan':
        return 'scan'
      case 'writingassistant':
        return 'schrijfassistent'
      case 'practicecase':
        return 'oefencasus'
      case 'user':
        return 'gebruiker'
      case 'task':
        return 'taak'
      case 'file':
        return 'bestand'
      case 'role':
        return 'rol'
      case 'permission':
        return 'recht'
      case 'apitoken':
        return 'API-token'
      case 'system':
        return 'systeemitem'
      default:
        return getResourceTypeLabel(log.resourceType) ?? 'item'
    }
  }

  const getResourceIdentifier = (log: AuditLog) => {
    const resourceType = log.resourceType?.toLowerCase()
    const shortId = formatShortResourceId(log.resourceId)
    const explicitTitle = getDetailString(log, 'title', 'name', 'scan_title')
    const location = getDetailString(log, 'location')
    const contentType = getDetailString(log, 'content_type')
    const descriptionIdentifier = getDescriptionIdentifier(log)

    if (resourceType === 'user' && log.resourceId) {
      const relatedUser = usersById.get(log.resourceId)
      if (relatedUser?.displayName?.trim()) {
        return relatedUser.displayName
      }

      const fullName = [relatedUser?.firstName, relatedUser?.lastName].filter(Boolean).join(' ').trim()
      if (fullName) {
        return fullName
      }

      if (relatedUser?.email) {
        return relatedUser.email
      }
    }

    if (resourceType === 'crisis' && log.resourceId) {
      const crisis = crisesById.get(log.resourceId)
      if (crisis) {
        return `${crisis.type} - ${crisis.location}`
      }
    }

    if (resourceType === 'scan') {
      if (explicitTitle) {
        return explicitTitle
      }

      if (location && shortId) {
        return `${location} (${shortId})`
      }

      if (location) {
        return location
      }

      return shortId ? `Scan ${shortId}` : null
    }

    if (resourceType === 'writingassistant') {
      if (contentType) {
        return humanizeToken(contentType)
      }

      return shortId ? `Item ${shortId}` : null
    }

    if (resourceType === 'bobanalyse' || resourceType === 'practicecase') {
      return explicitTitle ?? descriptionIdentifier ?? shortId
    }

    return explicitTitle ?? descriptionIdentifier ?? shortId
  }

  const getEventTitle = (log: AuditLog) => {
    const action = log.action.toUpperCase()
    const updatedFieldsSummary = formatListSummary(getDetailStringArray(log, 'updated_fields'))
    const loginMethod = getDetailString(log, 'login_method')
    const resourceEventLabel = getResourceEventLabel(log)

    if (action === 'AUTHZ_DENIED') {
      return 'Ontbrekende rechten'
    }

    if (action === 'USER_LOGIN') {
      return loginMethod === 'azure_sso' ? 'Azure AD SSO' : 'Succesvolle aanmelding'
    }

    if (action === 'USER_LOGOUT') {
      return 'Sessie beëindigd'
    }

    if (action.includes('UPDATED')) {
      return updatedFieldsSummary ?? `${resourceEventLabel} bijgewerkt`
    }

    if (action.includes('CREATED')) {
      return resourceEventLabel === 'BOB-analyse'
        ? 'Nieuwe BOB-analyse'
        : `Nieuwe ${resourceEventLabel}`
    }

    if (action.includes('DELETED')) {
      return `${resourceEventLabel} verwijderd`
    }

    if (action.includes('ACTIVATED')) {
      return `${resourceEventLabel} geactiveerd`
    }

    if (action.includes('DEACTIVATED')) {
      return `${resourceEventLabel} gedeactiveerd`
    }

    if (action.includes('ROLE_CHANGED')) {
      return 'Gebruikersrol gewijzigd'
    }

    return getActionLabel(log.action)
  }

  const getEventSubtitle = (log: AuditLog) => {
    const action = log.action.toUpperCase()
    const missingPermissions = formatListSummary(getDetailStringArray(log, 'missing_permissions'))
    const requiredPermissions = formatListSummary(getDetailStringArray(log, 'required_permissions'))
    const location = getDetailString(log, 'location')
    const section = getDetailString(log, 'section')
    const categoryName = getDetailString(log, 'category_name')

    if (action === 'AUTHZ_DENIED') {
      return missingPermissions ?? requiredPermissions ?? null
    }

    if (section && categoryName) {
      return `${humanizeToken(section)} / ${categoryName}`
    }

    if (location && log.resourceType?.toLowerCase() !== 'crisis') {
      return location
    }

    return null
  }

  const formatAbsoluteTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)

    if (isToday(date)) {
      return `Vandaag, ${format(date, 'HH:mm', { locale: nl })}`
    }

    if (isYesterday(date)) {
      return `Gisteren, ${format(date, 'HH:mm', { locale: nl })}`
    }

    return format(date, 'd MMM yyyy, HH:mm', { locale: nl })
  }

  // Filter logs by search query
  const filteredLogs = logsData?.logs.filter((log: AuditLog) => {
    const query = searchQuery.toLowerCase()

    if (!query) {
      return true
    }

    return (
      getEventTitle(log).toLowerCase().includes(query) ||
      getActionLabel(log.action).toLowerCase().includes(query) ||
      getActionChipLabel(log.action).toLowerCase().includes(query) ||
      getUserDisplayName(log).toLowerCase().includes(query) ||
      (getUserSecondaryLabel(log)?.toLowerCase().includes(query) ?? false) ||
      (getEventSubtitle(log)?.toLowerCase().includes(query) ?? false) ||
      (getResourceTypeLabel(log.resourceType)?.toLowerCase().includes(query) ?? false) ||
      (getResourceIdentifier(log)?.toLowerCase().includes(query) ?? false) ||
      (log.description?.toLowerCase().includes(query) ?? false) ||
      (log.resourceType?.toLowerCase().includes(query) ?? false)
    )
  })

  const getActionBadgeColor = (action: string) => {
    const normalizedAction = action.toLowerCase()

    // Color code based on action type
    if (normalizedAction.includes('created')) return 'bg-green-100 text-green-800'
    if (normalizedAction.includes('updated')) return 'bg-blue-100 text-blue-800'
    if (normalizedAction.includes('deleted')) return 'bg-red-100 text-red-800'
    if (normalizedAction.includes('denied')) return 'bg-amber-100 text-amber-800'
    if (normalizedAction.includes('login')) return 'bg-purple-100 text-purple-800'
    if (normalizedAction.includes('logout')) return 'bg-gray-100 text-gray-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getResourceTypeIcon = (resourceType: string | null) => {
    switch (resourceType?.toLowerCase()) {
      case 'user':
        return <User className="h-4 w-4" />
      case 'crisis':
      case 'endpoint':
      case 'role':
      case 'permission':
      case 'apitoken':
      case 'system':
        return <Shield className="h-4 w-4" />
      case 'scan':
      case 'task':
      case 'bobanalyse':
        return <Activity className="h-4 w-4" />
      case 'writingassistant':
      case 'practicecase':
        return <FileText className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  useEffect(() => {
    const params = new URLSearchParams()

    if (searchQuery) params.set('q', searchQuery)
    if (currentPage > 1) params.set('page', String(currentPage))
    if (perPage !== DEFAULT_PER_PAGE) params.set('per_page', String(perPage))

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        params.set(key, value)
      }
    })

    const nextQuery = params.toString()
    const nextUrl = nextQuery ? `${window.location.pathname}?${nextQuery}` : window.location.pathname
    window.history.replaceState({}, '', nextUrl)
  }, [searchQuery, currentPage, perPage, filters])

  const handleViewClick = (log: AuditLog) => {
    setSelectedLog(log)
    setIsViewDialogOpen(true)
  }

  const handleExportClick = () => {
    setIsExportDialogOpen(true)
  }

  const handleExportSubmit = async () => {
    try {
      const blob = await exportLogs({
        ...Object.fromEntries(
          Object.entries(filters).filter(([, value]) => value !== '' && value !== 'all')
        ),
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit_logs_export_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setIsExportDialogOpen(false)
      toast({
        title: t('admin.audit.exportSuccess'),
        description: t('admin.audit.exportSuccessDesc'),
      })
    } catch (error) {
      toast({
        title: t('admin.audit.exportError'),
        description: error instanceof Error ? error.message : t('admin.audit.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page when filters change
  }

  const clearFilters = () => {
    setFilters({
      user_id: '',
      crisis_id: '',
      action: 'all',
      resource_type: 'all',
      start_date: '',
      end_date: '',
    })
    setCurrentPage(1)
  }

  const handlePerPageChange = (value: string) => {
    setPerPage(Number(value))
    setCurrentPage(1)
  }

  const totalPages = logsData ? Math.ceil(logsData.total / perPage) : 1

  return (
    <Layout>
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                {t('admin.audit.title')}
              </h1>
              <p className="text-muted-foreground mt-2">{t('admin.audit.description')}</p>
            </div>
            <div className="flex space-x-2">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('admin.vernieuwen')}
              </Button>
              {canExportAudit && (
                <Button onClick={handleExportClick} size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  {t('admin.audit.export')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative bg-white flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('admin.audit.searchPlaceholder')}
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="per_page" className="whitespace-nowrap">
                {t('admin.audit.perPage')}
              </Label>
              <Select value={String(perPage)} onValueChange={handlePerPageChange}>
                <SelectTrigger id="per_page" className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PER_PAGE_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Search className="h-5 w-5" />
              <span>{t('admin.audit.filters')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="user_id">{t('admin.audit.user')}</Label>
                <Select
                  value={filters.user_id}
                  onValueChange={value => handleFilterChange('user_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectUser')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allUsers')}</SelectItem>
                    {usersData?.users?.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.displayName || `${user.firstName} ${user.lastName}`} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="crisis_id">{t('admin.audit.crisis')}</Label>
                <Select
                  value={filters.crisis_id}
                  onValueChange={value => handleFilterChange('crisis_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectCrisis')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allCrises')}</SelectItem>
                    {crisesData?.data?.map(crisis => (
                      <SelectItem key={crisis.id} value={crisis.id}>
                        {crisis.type} - {crisis.location} (
                        {new Date(crisis.createdAt || '').toLocaleDateString('nl-NL')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="action">{t('admin.audit.action')}</Label>
                <Select
                  value={filters.action}
                  onValueChange={value => handleFilterChange('action', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectAction')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allActions')}</SelectItem>
                    {actionsData?.actions.map(action => (
                      <SelectItem key={action} value={action}>
                        {action.replace(/_/g, ' ').toLowerCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="resource_type">{t('admin.audit.resourceType')}</Label>
                <Select
                  value={filters.resource_type}
                  onValueChange={value => handleFilterChange('resource_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectResourceType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allResourceTypes')}</SelectItem>
                    {AUDIT_RESOURCE_TYPES.map(resourceType => (
                      <SelectItem key={resourceType} value={resourceType}>
                        {getResourceTypeLabel(resourceType)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="start_date">{t('admin.audit.startDate')}</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={filters.start_date}
                  onChange={e => handleFilterChange('start_date', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="end_date">{t('admin.audit.endDate')}</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={filters.end_date}
                  onChange={e => handleFilterChange('end_date', e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={clearFilters}>
                {t('admin.audit.clearFilters')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('admin.audit.totalLogs')}</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{logsData?.total || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('admin.audit.todayLogs')}</CardTitle>
              <Calendar className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {logsData?.logs.filter((log: AuditLog) => {
                  const logDate = new Date(log.createdAt)
                  const today = new Date()
                  return logDate.toDateString() === today.toDateString()
                }).length || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('admin.audit.userActions')}</CardTitle>
              <User className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {logsData?.logs.filter((log: AuditLog) => log.action.includes('user_')).length || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t('admin.audit.systemActions')}
              </CardTitle>
              <Shield className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {logsData?.logs.filter(
                  (log: AuditLog) => log.action.includes('login') || log.action.includes('logout')
                ).length || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audit Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>{t('admin.audit.auditLogs')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                {t('admin.audit.loading')}
              </div>
            ) : filteredLogs && filteredLogs.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <FileText className="h-6 w-6 text-muted-foreground mr-2" />
                {t('admin.audit.noLogs')}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.audit.action')}</TableHead>
                      <TableHead>{t('admin.audit.user')}</TableHead>
                      <TableHead>{t('admin.audit.resource')}</TableHead>
                      <TableHead>{t('admin.audit.timestamp')}</TableHead>
                      <TableHead className="w-14 text-right">{t('admin.audit.details')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs?.map((log: AuditLog) => {
                      const actorName = getUserDisplayName(log)
                      const actorSecondaryLabel = getUserSecondaryLabel(log)
                      const resourceTypeLabel = getResourceTypeLabel(log.resourceType) ?? 'Systeem'
                      const resourceIdentifier = getResourceIdentifier(log)
                      const eventTitle = getEventTitle(log)
                      const eventSubtitle = getEventSubtitle(log)

                      return (
                        <TableRow key={log.id}>
                          <TableCell className="py-3">
                            <div className="flex items-start gap-3 min-w-0">
                              <div className="mt-0.5 text-muted-foreground">
                                {getResourceTypeIcon(log.resourceType)}
                              </div>
                              <div className="min-w-0 space-y-1">
                                <Badge className={getActionBadgeColor(log.action)}>
                                  {getActionChipLabel(log.action)}
                                </Badge>
                                <p className="font-medium leading-5 truncate max-w-[28rem]">
                                  {eventTitle}
                                </p>
                                {eventSubtitle && (
                                  <p className="text-xs text-muted-foreground truncate max-w-[28rem]">
                                    {eventSubtitle}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-3 align-top">
                            <div className="min-w-0">
                              <p className="font-medium truncate max-w-[14rem]">{actorName}</p>
                              {actorSecondaryLabel && (
                                <p className="text-xs text-muted-foreground truncate max-w-[14rem]">
                                  {actorSecondaryLabel}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 align-top">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate max-w-[16rem]">
                                {resourceTypeLabel}
                              </p>
                              {resourceIdentifier && resourceIdentifier !== resourceTypeLabel && (
                                <p className="text-xs text-muted-foreground truncate max-w-[16rem]">
                                  {resourceIdentifier}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 align-top whitespace-nowrap">
                            <p className="font-medium">{formatAbsoluteTimestamp(log.createdAt)}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(log.createdAt), {
                                addSuffix: true,
                                locale: nl,
                              })}
                            </p>
                          </TableCell>
                          <TableCell className="py-3 text-right align-top">
                            <Button variant="ghost" size="sm" onClick={() => handleViewClick(log)}>
                              <Eye className="h-4 w-4" />
                              <span className="sr-only">{t('admin.audit.logDetails')}</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      {t('admin.audit.showing')} {(currentPage - 1) * perPage + 1} -{' '}
                      {Math.min(currentPage * perPage, logsData?.total || 0)} {t('admin.audit.of')}{' '}
                      {logsData?.total || 0}
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        {t('admin.audit.previous')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        {t('admin.audit.next')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Log Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.audit.logDetails')}</DialogTitle>
            <DialogDescription>{t('admin.audit.logDetailsDescription')}</DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.action')}</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge className={getActionBadgeColor(selectedLog.action)}>
                      {selectedLog.action.replace(/_/g, ' ').toLowerCase()}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.timestamp')}</Label>
                  <p className="font-medium">
                    {formatDistanceToNow(new Date(selectedLog.createdAt), {
                      addSuffix: true,
                      locale: nl,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(selectedLog.createdAt).toLocaleString('nl-NL')}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.userId')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.userId}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.crisisId')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.crisisId || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.resourceType')}</Label>
                  <p className="font-medium">{selectedLog.resourceType || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.resourceId')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.resourceId || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.ipAddress')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.ipAddress || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.requestMethod')}</Label>
                  <p className="font-medium">{selectedLog.requestMethod || '-'}</p>
                </div>
              </div>

              {selectedLog.description && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.description')}</Label>
                  <p className="mt-1">{selectedLog.description}</p>
                </div>
              )}

              {selectedLog.requestPath && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.requestPath')}</Label>
                  <p className="font-mono text-sm mt-1">{selectedLog.requestPath}</p>
                </div>
              )}

              {selectedLog.userAgent && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.userAgent')}</Label>
                  <p className="text-sm mt-1 break-all">{selectedLog.userAgent}</p>
                </div>
              )}

              {selectedLog.details && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.details')}</Label>
                  <pre className="mt-1 p-2 bg-muted rounded text-sm overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              {t('admin.audit.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.audit.exportLogs')}</DialogTitle>
            <DialogDescription>{t('admin.audit.exportLogsDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('admin.audit.exportCurrentFilters')}</p>
            {Object.entries(filters).some(([, value]) => value && value !== 'all') && (
              <div className="p-3 bg-muted rounded">
                <p className="text-sm font-medium mb-2">{t('admin.audit.activeFilters')}:</p>
                <div className="space-y-1">
                  {Object.entries(filters)
                    .filter(([, value]) => value && value !== 'all')
                    .map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="font-medium">{key}:</span> {value}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsExportDialogOpen(false)}
              disabled={isExporting}
            >
              {t('admin.audit.cancel')}
            </Button>
            <Button onClick={handleExportSubmit} disabled={isExporting}>
              {isExporting ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t('admin.audit.export')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
